"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _View = _interopRequireDefault(require("react-native-web/dist/cjs/exports/View"));
var _Platform = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Platform"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _ThemeProvider = require("../ThemeProvider");
var _utils = require("../utils");
var _TextInputBase = _interopRequireDefault(require("../TextInput/TextInputBase"));
var _ButtonBase = _interopRequireDefault(require("../Button/ButtonBase"));
var _useCopy = _interopRequireDefault(require("../utils/useCopy"));
var _dictionary = _interopRequireDefault(require("./dictionary"));
var _Icon = _interopRequireDefault(require("../Icon"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectContainerProps, selectedContainerPropTypes] = (0, _utils.selectSystemProps)([_utils.a11yProps, _utils.viewProps, _utils.contentfulProps]);
const [selectInputProps, selectedInputPropTypes] = (0, _utils.selectSystemProps)([_utils.textInputHandlerProps, _utils.textInputProps]);
const selectInputTokens = _ref => {
  let {
    searchTokens,
    buttonTokens,
    buttonsGapSize
  } = _ref;
  const {
    paddingRight: inputPaddingRight,
    clearButtonIcon,
    submitButtonIcon
  } = searchTokens;
  const {
    paddingLeft = 0,
    paddingRight = 0,
    iconSize = 0
  } = buttonTokens;
  const buttonWidth = paddingLeft + paddingRight + iconSize;
  const paddingWithButtons = inputPaddingRight + (submitButtonIcon ? buttonWidth : 0) + (clearButtonIcon ? buttonsGapSize + buttonWidth : 0);
  return {
    ...(0, _utils.selectTokens)('TextInput', searchTokens),
    paddingRight: paddingWithButtons
  };
};
const selectButtonTokens = tokens => (0, _utils.selectTokens)('SearchButton', tokens);
const selectIconTokens = _ref2 => {
  let {
    iconSize,
    iconColor
  } = _ref2;
  return {
    color: iconColor,
    size: iconSize
  };
};
const selectBorderTokens = _ref3 => {
  let {
    borderWidth,
    mobileBorderWidth,
    ...tokens
  } = _ref3;
  return {
    borderWidth: _Platform.default.OS === 'web' ? borderWidth : mobileBorderWidth,
    ...tokens
  };
};
const modifyButtonTokens = tokens => {
  const modifiedTokens = {
    ...tokens
  };
  if (_Platform.default.OS !== 'web') {
    modifiedTokens.backgroundColor = tokens.mobileBackgroundColor;
  }
  return modifiedTokens;
};

/**
 * The `Search` component is a combination of a `TextInput` and 2 different kinds of custom buttons.
 * Use `Search` to feature autocomplete interactions.
 *
 * This component includes a clear button, which will appear as text is entered and pressing it will
 * cause the input's internal state value to be set to an empty string.
 *
 * This component may only be used as uncontrolled, as the search value is managed within an internal state.
 * You can use the `onChange` prop to react to value changes and the `onSubmit` prop to react on search submission.
 *
 * Use the following props to supply additional accessibility labels for the input - `accessibilityLabel`,
 * clear button - `clearButtonAccessibilityLabel`, and submit button - `submitButtonAccessibilityLabel`.
 */
const Search = /*#__PURE__*/_react.default.forwardRef((_ref4, ref) => {
  let {
    initialValue,
    value,
    placeholder,
    inactive,
    onChange,
    onSubmit,
    onClear,
    onFocus,
    accessibilityLabel,
    copy = 'en',
    tokens,
    variant,
    nativeSubmitBtnID,
    searchIconPosition = 'right',
    dataSet,
    ...rest
  } = _ref4;
  const {
    currentValue = '',
    setValue
  } = (0, _utils.useInputValue)({
    value,
    initialValue,
    onChange
  });
  const themeTokens = (0, _ThemeProvider.useThemeTokens)('Search', tokens, {
    inactive,
    ...variant
  });
  const buttonTokens = (0, _ThemeProvider.useThemeTokens)('SearchButton', tokens, variant);
  const getThemeTokens = (0, _ThemeProvider.useThemeTokensCallback)('Search', tokens, variant);
  const getButtonTokens = (0, _ThemeProvider.useThemeTokensCallback)('SearchButton', tokens, variant);

  // TODO: support overriding the a11y props using a custom dictionary
  const getCopy = (0, _useCopy.default)({
    dictionary: _dictionary.default,
    copy
  });
  const {
    placeholderColor,
    buttonsGap,
    clearButtonIcon: ClearButtonIcon,
    submitButtonIcon: SubmitButtonIcon
  } = themeTokens;

  // get the actual gap value for the current viewport
  const buttonsGapSize = (0, _utils.useSpacingScale)(buttonsGap);
  const handleSubmit = event => {
    if (onSubmit !== undefined) {
      onSubmit(currentValue, event);
    }
  };
  const handleClear = event => {
    setValue('', event);
    onClear?.('', event);
    ref?.current?.focus();
  };
  const handleFocus = event => {
    if (onFocus !== undefined) onFocus(event);
  };
  const isEmpty = currentValue === '';

  // Accessibility label should always be present and correctly localised
  const a11yLabelText = accessibilityLabel || getCopy('accessibilityLabel');
  // Placeholder is optional and may be unset by passing an empty string
  const placeholderText = placeholder ?? a11yLabelText;
  const {
    nativeID,
    testID,
    ...containerProps
  } = selectContainerProps(rest);
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
    ...containerProps,
    dataSet: dataSet,
    children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_TextInputBase.default, {
      nativeID: nativeID,
      testID: testID,
      ...selectInputProps(rest),
      ref: ref,
      tokens: pressableStates => selectInputTokens({
        searchTokens: selectBorderTokens(getThemeTokens(pressableStates)),
        buttonTokens,
        buttonsGapSize,
        isEmpty
      }),
      placeholder: placeholderText,
      placeholderTextColor: placeholderColor,
      inactive: inactive,
      enablesReturnKeyAutomatically: true,
      returnKeyType: "search",
      value: currentValue,
      onChange: setValue,
      onSubmitEditing: handleSubmit,
      onFocus: handleFocus,
      accessibilityLabel: a11yLabelText,
      direction: searchIconPosition,
      buttons: [ClearButtonIcon && !isEmpty && /*#__PURE__*/(0, _jsxRuntime.jsx)(_ButtonBase.default, {
        accessibilityLabel: getCopy('clearButtonAccessibilityLabel'),
        accessibilityRole: "button",
        inactive: inactive,
        onPress: handleClear,
        tokens: pressableStates => modifyButtonTokens(selectButtonTokens(getButtonTokens(pressableStates))),
        children: buttonState => /*#__PURE__*/(0, _jsxRuntime.jsx)(ClearButtonIcon, {
          ...selectIconTokens(getButtonTokens(buttonState))
        })
      }, "clear"), SubmitButtonIcon && (searchIconPosition === 'right' ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_ButtonBase.default, {
        accessibilityLabel: getCopy('submitButtonAccessibilityLabel'),
        accessibilityRole: "button",
        inactive: inactive,
        onPress: handleSubmit,
        tokens: buttonState => selectButtonTokens(getButtonTokens({
          ...buttonState,
          priority: 'high'
        })),
        nativeID: nativeSubmitBtnID,
        children: buttonState => /*#__PURE__*/(0, _jsxRuntime.jsx)(SubmitButtonIcon, {
          ...selectIconTokens(getButtonTokens({
            ...buttonState,
            priority: 'high'
          }))
        })
      }, "submit-right") : /*#__PURE__*/(0, _jsxRuntime.jsx)(_Icon.default, {
        icon: SubmitButtonIcon,
        testID: "iconLeft",
        tokens: {
          color: themeTokens.iconLeftColor,
          size: themeTokens.iconLeftSize
        }
      }, "submitIcon"))]
    })
  });
});
Search.displayName = 'Search';
Search.propTypes = {
  ...selectedContainerPropTypes,
  ...selectedInputPropTypes,
  /**
   * Use this to set the initial value of the search input.
   * Updating `initialValue` will **not** update the actual value.
   */
  initialValue: _propTypes.default.string,
  /**
   * Label rendered in the search input when it has no value.
   */
  placeholder: _propTypes.default.string,
  /**
   * Disables all user interactions with the search input.
   */
  inactive: _propTypes.default.bool,
  /**
   * Use to react upon search input's value changes.
   * Will receive the searched value as an argument.
   */
  onChange: _propTypes.default.func,
  /**
   * Triggered when the search input is submitted,
   * either by pressing the submit button, "Enter" key on web,
   * or "Search" key in a mobile keyboard.
   */
  onSubmit: _propTypes.default.func,
  /**
   * Triggered when the clear button is pressed.
   */
  onClear: _propTypes.default.func,
  /**
   * Triggered when the search input is focused.
   */
  onFocus: _propTypes.default.func,
  /**
   *
   * Use to provide an accessible label for the input (visually hidden).
   */
  accessibilityLabel: _propTypes.default.string,
  /**
   * Select English or French copy for the accessible labels.
   * You may also pass in a custom dictionary object.
   */
  copy: _propTypes.default.oneOfType([_propTypes.default.oneOf(['en', 'fr']), _propTypes.default.shape({
    accessibilityLabel: _propTypes.default.string,
    clearButtonAccessibilityLabel: _propTypes.default.string,
    submitButtonAccessibilityLabel: _propTypes.default.string
  })]),
  tokens: (0, _utils.getTokensPropType)('Search'),
  variant: _utils.variantProp.propType,
  /**
   * ID for Submit button element on web
   */
  nativeSubmitBtnID: _propTypes.default.string
};
var _default = exports.default = Search;